import React, { useRef } from "react";
import InputWrapper from "./InputWrapper";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import debounce from "lodash.debounce";
import dayjs from "dayjs";
import { checkDateWarning } from "../../../App/Pages/Projects/ProjectDetail/ProjectJobsForm";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ReadOnlyInput from "./ReadOnlyInput";
import { Controller } from "react-hook-form";
import { DEFAULT_DATE_FORMAT, DEFAULT_SIZE } from "../../../utils/constant";
import PropTypes from "prop-types";
export default function DateField({
  size = DEFAULT_SIZE,
  mode,
  isAddMode,
  label,
  control,
  warningLabel,
  name,
  rules,
  shouldDisableDate,
  fieldProps = {},
  ...rest
}) {
  const textFieldRef = useRef(null);
  const { canView = true, canAdd = true, canEdit = true } = rest.permission || {};
  if (!canView || (isAddMode && !canAdd)) return null;
  const editModeAllowed = isAddMode ? canAdd : canEdit;
  if (mode === "read" || (mode === "edit" && !editModeAllowed)) return <ReadOnlyInput name={name} label={label} size={size} type="date" {...rest} rules={rules}/>;
  const debouncedWarn = debounce((date) => {
    if (date && dayjs(date).isValid() && warningLabel && warningLabel.trim()) {
      checkDateWarning(date, warningLabel);
    }
  }, 500);
  return (
    <InputWrapper size={size}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Controller
          control={control}
          name={name}
          rules={{
            validate: value => {
              if (!value) return true;
              if (value.isValid && !value.isValid()) return "Enter valid date";
            },
            ...(rules || {})
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DatePicker
              slotProps={{
                textField: {
                  InputLabelProps: { shrink: true },
                  fullWidth: true,
                  error: !!error,
                  helperText: error?.message || "",
                  inputRef: input => {
                    textFieldRef.current = input;
                  },
                  onBlur: () => {
                    const inputValue = textFieldRef.current?.value;
                    const parsed = dayjs(inputValue, DEFAULT_DATE_FORMAT, true); // strict parsing
                    if (parsed.isValid() && warningLabel && warningLabel.trim()) {
                      checkDateWarning(parsed, warningLabel);
                    }
                  }
                }
              }}
              inputFormat={DEFAULT_DATE_FORMAT}
              onChange={(newValue) => {
                onChange(newValue);
                debouncedWarn(newValue);
              }}
              onClose={() => {
                if (value && dayjs(value).isValid() && warningLabel && warningLabel.trim()) {
                  checkDateWarning(value, warningLabel);
                }
              }}
              shouldDisableDate={shouldDisableDate}
              value={value}
               label={
                <span>
                  {label}
                  {rules?.required && <span style={{ color: "red" }}> *</span>}
                </span>
              }
              {...fieldProps}
            />
          )}
        />
      </LocalizationProvider>
    </InputWrapper>
  );
}

DateField.propTypes = {
  name: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["read", "edit"]).isRequired,
  label: PropTypes.string.isRequired,
  rules: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  isAddMode: PropTypes.bool,
  size: PropTypes.string,
  shouldDisableDate: PropTypes.func,
  fieldProps: PropTypes.object,
  permission: PropTypes.shape({
    canView: PropTypes.bool,
    canAdd: PropTypes.bool,
    canEdit: PropTypes.bool
  })
};
